import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { MonthInput } from "@/components/DateInput";
import { useAsyncData } from "@/lib/useAsyncData";
import { ReportError, ReportLoading } from "@/components/leasing/ReportState";
import { fmtNumber, fmtMonth, fmtMonthShort, fmtAbbrev } from "@/lib/format";
import HelpButton from "@/components/HelpButton";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer,
  AreaChart, Area, LabelList,
} from "recharts";

const NAVY = "#111827";
const YELLOW = "#FFC107";
const NAVY_SOFT = "#b9c0cb"; // light navy for non-highlighted bars
const GRID = "#eef0f3";

function lastClosedYm() {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const EMPTY = "Nicio dată pentru luna aleasă";

function Empty({ h = 200 }) {
  return <div className="flex items-center justify-center text-sm text-slate-400" style={{ height: h }}>{EMPTY}</div>;
}

function Card({ title, subtitle, help, span = "", onClick, children }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white p-6 ${span} ${onClick ? "cursor-pointer transition-colors hover:border-slate-300" : ""}`}
      onClick={onClick} data-testid={onClick ? undefined : undefined}>
      <div className="flex items-start justify-between mb-4 gap-2">
        <div>
          <h2 className="font-display font-semibold text-base text-slate-900">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5 tabular">{subtitle}</p>}
        </div>
        {help && <HelpButton anchor={help} />}
      </div>
      {children}
    </div>
  );
}

function Delta({ kpi }) {
  if (!kpi || kpi.delta_abs === null || kpi.delta_abs === undefined) {
    return <div className="text-xs text-slate-400 mt-1 h-4" data-testid="kpi-delta">—</div>;
  }
  const up = kpi.delta_abs > 0, down = kpi.delta_abs < 0;
  const arrow = up ? "▲" : down ? "▼" : "▬";
  const pct = kpi.delta_pct === null || kpi.delta_pct === undefined ? "" : ` (${fmtNumber(Math.abs(kpi.delta_pct), 1)}%)`;
  return (
    <div className="text-xs text-slate-500 mt-1 h-4 tabular" data-testid="kpi-delta">
      <span className="mr-1">{arrow}</span>{fmtNumber(Math.abs(kpi.delta_abs))}{pct}
      <span className="text-slate-400"> vs. luna anterioară</span>
    </div>
  );
}

function KpiTile({ label, kpi, months12, onClick, testid }) {
  const spark = kpi?.spark || []; // [{m, v, has_data}]
  return (
    <div data-testid={testid} onClick={onClick}
      className="rounded-xl border border-slate-200 bg-white p-6 cursor-pointer transition-colors hover:border-slate-300 lg:col-span-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      {kpi?.has_data ? (
        <>
          <div className="text-2xl xl:text-3xl font-bold text-slate-900 tabular mt-1.5" data-testid={`${testid}-value`}>
            {fmtNumber(kpi.value)}
          </div>
          <Delta kpi={kpi} />
          <div className="mt-3 -mx-1" style={{ height: 44 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={spark} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <defs>
                  <linearGradient id={`sg-${testid}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={NAVY} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={NAVY} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <Tooltip cursor={false} content={({ active, payload }) =>
                  active && payload?.length && payload[0].value !== null ? (
                    <div className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs shadow-sm">
                      <div className="text-slate-500">{fmtMonth(payload[0].payload.m)}</div>
                      <div className="tabular font-semibold">{fmtNumber(payload[0].value)}</div>
                    </div>
                  ) : null} />
                <Area type="monotone" dataKey="v" connectNulls={false} stroke={NAVY} strokeWidth={1.75} fill={`url(#sg-${testid})`} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <div className="text-sm text-slate-400 mt-4" style={{ minHeight: 92 }}>{EMPTY}</div>
      )}
    </div>
  );
}

function MonthTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((a, p) => a + (p.value || 0), 0);
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
      <div className="font-semibold mb-1">{fmtMonth(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex justify-between gap-6">
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="tabular">{fmtNumber(p.value)}</span>
        </div>
      ))}
      {payload.length > 1 && (
        <div className="flex justify-between gap-6 border-t mt-1 pt-1 font-semibold">
          <span>Total</span><span className="tabular">{fmtNumber(total)}</span>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const [ym, setYm] = useState(lastClosedYm());
  const [finFilter, setFinFilter] = useState("all");
  const navigate = useNavigate();

  const { loading, error, data, reload } = useAsyncData(async () => {
    if (!ym) return null;
    const res = await api.get("/dashboard/overview", { params: { month: ym } });
    return res.data;
  }, [ym]);

  const k = data?.kpis;
  const months12 = data?.months12 || [];

  const FIN_CHIPS = [["all", "Toți finanțatorii"], ["DL", "DL"], ["BP", "BP"], ["RL", "RL"], ["PL", "PL"]];
  const fwd = data?.leasing_forward;
  const fwdData = (finFilter === "all" ? (fwd?.all || []) : (fwd?.by_financer?.[finFilter] || []))
    .map((r) => ({ ...r, total: Math.round((r.principal + r.interest) * 100) / 100 }));

  const capFin = (data?.capital_by_financer || []).map((r) => ({
    ...r, label: `${fmtAbbrev(r.value)} · ${fmtNumber(r.pct, 1)}%`,
  }));
  const prepaidMonthly = data?.prepaid_monthly || [];
  const fuelTop = data?.fuel_top;

  return (
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 -my-6 px-4 sm:px-6 lg:px-8 py-6 bg-slate-50 min-h-[calc(100vh-4rem)]">
      <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Panou lunar</h1>
          <HelpButton anchor="panou" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase text-slate-500">Lună (LL-AAAA)</Label>
          <MonthInput value={ym} onChange={setYm} className="w-40" data-testid="dashboard-month" />
        </div>
      </div>

      {error ? <ReportError onRetry={reload} /> : loading && !data ? <ReportLoading label="Se încarcă panoul…" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="dashboard-grid">
          {/* Row 1 — KPI tiles */}
          <KpiTile testid="kpi-capital" label="Capital rămas leasing" kpi={k?.capital} months12={months12} onClick={() => navigate("/leasing")} />
          <KpiTile testid="kpi-dobanda" label="Dobândă rămasă leasing" kpi={k?.dobanda} months12={months12} onClick={() => navigate("/leasing")} />
          <KpiTile testid="kpi-vrl" label="Cheltuieli în avans · repartizat în lună" kpi={k?.vrl} months12={months12} onClick={() => navigate("/cheltuieli-in-avans")} />
          <KpiTile testid="kpi-fuel" label="Carburanți · 60221 FINAL" kpi={k?.fuel_final} months12={months12} onClick={() => navigate("/carburanti")} />

          {/* Row 2 */}
          <div className="lg:col-span-8">
            <Card title="Plăți leasing — următoarele 12 luni" help="rapoarte-leasing">
              <div className="flex flex-wrap gap-1.5 mb-4" data-testid="leasing-fin-chips">
                {FIN_CHIPS.map(([code, lbl]) => (
                  <button key={code} data-testid={`chip-${code}`} onClick={() => setFinFilter(code)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${finFilter === code ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
              {fwdData.length === 0 ? <Empty h={300} /> : (
                <div style={{ height: 300 }} className="cursor-pointer" onClick={() => navigate("/leasing")} data-testid="leasing-forward-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={fwdData} margin={{ top: 22, right: 8, bottom: 4, left: 8 }}>
                      <XAxis dataKey="m" tickFormatter={fmtMonthShort} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: GRID }} tickLine={false} />
                      <YAxis tickFormatter={fmtAbbrev} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={54} />
                      <Tooltip cursor={{ fill: "#f1f5f9" }} content={<MonthTooltip />} />
                      <Bar dataKey="principal" name="Capital" stackId="a" fill={NAVY} radius={[0, 0, 0, 0]} maxBarSize={42} />
                      <Bar dataKey="interest" name="Dobândă" stackId="a" fill={YELLOW} radius={[3, 3, 0, 0]} maxBarSize={42}>
                        <LabelList dataKey="total" position="top" formatter={fmtAbbrev} style={{ fontSize: 10, fill: "#475569" }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          <div className="lg:col-span-4">
            <Card title="Capital rămas pe finanțator" help="rapoarte-leasing">
              {capFin.length === 0 || (k && !k.capital.has_data) ? <Empty h={300} /> : (
                <div style={{ height: 300 }} className="cursor-pointer" onClick={() => navigate("/leasing")} data-testid="capital-financer-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={capFin} layout="vertical" margin={{ top: 4, right: 92, bottom: 4, left: 4 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="code" tick={{ fontSize: 12, fill: "#334155" }} axisLine={false} tickLine={false} width={34} />
                      <Tooltip cursor={{ fill: "#f1f5f9" }} content={({ active, payload }) =>
                        active && payload?.length ? (
                          <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
                            <div className="font-semibold">{payload[0].payload.name}</div>
                            <div className="tabular">{fmtNumber(payload[0].value)} · {fmtNumber(payload[0].payload.pct, 1)}%</div>
                          </div>) : null} />
                      <Bar dataKey="value" fill={NAVY} radius={[0, 4, 4, 0]} maxBarSize={30}>
                        <LabelList dataKey="label" position="right" offset={8}
                          style={{ fontSize: 10, fill: "#475569" }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          {/* Row 3 */}
          <div className="lg:col-span-6">
            <Card title="Cheltuieli în avans — repartizat lunar" help="p1">
              {prepaidMonthly.length === 0 || (k && !k.vrl.has_data) ? <Empty h={280} /> : (
                <div style={{ height: 280 }} className="cursor-pointer" onClick={() => navigate("/cheltuieli-in-avans")} data-testid="prepaid-monthly-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={prepaidMonthly} margin={{ top: 12, right: 8, bottom: 4, left: 8 }}>
                      <XAxis dataKey="m" tickFormatter={fmtMonthShort} tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: GRID }} tickLine={false} />
                      <YAxis tickFormatter={fmtAbbrev} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={54} />
                      <Tooltip cursor={{ fill: "#f1f5f9" }} content={({ active, payload, label }) =>
                        active && payload?.length ? (
                          <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
                            <div className="font-semibold mb-0.5">{fmtMonth(label)}</div>
                            <div className="tabular">VRL: {fmtNumber(payload[0].value)}</div>
                          </div>) : null} />
                      <Bar dataKey="vrl" name="VRL" radius={[3, 3, 0, 0]} maxBarSize={38}>
                        {prepaidMonthly.map((r) => <Cell key={r.m} fill={r.m === ym ? YELLOW : NAVY_SOFT} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          <div className="lg:col-span-6">
            <Card title="Carburanți — top centre de cost" help="f1"
              subtitle={fuelTop?.has_data ? `Bonus ${fmtNumber(fuelTop.bonus)} · rată ${fmtNumber(fuelTop.rate, 9)}` : null}>
              {!fuelTop?.has_data || (fuelTop.items || []).length === 0 ? <Empty h={280} /> : (
                <div style={{ height: 280 }} className="cursor-pointer" onClick={() => navigate("/carburanti")} data-testid="fuel-top-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={fuelTop.items} layout="vertical" margin={{ top: 4, right: 80, bottom: 4, left: 8 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="cc" tick={{ fontSize: 11, fill: "#334155" }} axisLine={false} tickLine={false} width={54} />
                      <Tooltip cursor={{ fill: "#f1f5f9" }} content={({ active, payload }) =>
                        active && payload?.length ? (
                          <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
                            <div className="font-semibold">CC {payload[0].payload.cc}</div>
                            <div className="tabular">{fmtNumber(payload[0].value)}</div>
                          </div>) : null} />
                      <Bar dataKey="value" name="60221 FINAL" radius={[0, 4, 4, 0]} maxBarSize={22}>
                        {fuelTop.items.map((r) => <Cell key={r.cc} fill={r.cc === "Altele" ? NAVY_SOFT : NAVY} />)}
                        <LabelList dataKey="value" position="right" formatter={fmtAbbrev} style={{ fontSize: 10, fill: "#475569" }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
